// ============================================
// BOTONARCHIVARCLIENTE.JS
// ============================================
// Botón "Archivar" reutilizable para Ficha* (Grupo / Razón social / Sucursal /
// Persona). Solo visible para usuario con rol 'direccion' Y entidad no archivada.
// Maneja el modal de confirmación, llama al DELETE correspondiente con
// header Authorization, traduce el detalle de 409 a mensaje natural.
//
// Nota sobre el rol: el backend lo guarda como 'direccion' (lowercase),
// pero en el login el frontend lo mapea a 'Dirección' (capitalizado, con
// acento) antes de meterlo al contexto. Normalizamos en este componente
// para aceptar ambos formatos sin depender de cuál llegue.

import React, { useState } from 'react';
import { useUsuario } from '../context/UsuarioContext';
import { API_BASE_CLI } from './helpersClientes';

function esRolDireccion(usuario) {
  const r = usuario?.rol;
  if (!r || typeof r !== 'string') return false;
  // Lowercase + sin acentos → "direccion" en cualquier formato.
  const norm = r.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return norm === 'direccion';
}

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const TIPO_META = {
  grupo:        { path: 'grupos',           label: 'grupo (cliente)' },
  razon_social: { path: 'razones-sociales', label: 'razón social' },
  sucursal:     { path: 'sucursales',       label: 'sucursal' },
  persona:      { path: 'personas',         label: 'persona' },
};

const DETALLE_HUMANO = {
  razones_sociales_activas:    (n) => `${n} razon${n === 1 ? '' : 'es'} sociales activas`,
  sucursales_activas:          (n) => `${n} sucursal${n === 1 ? '' : 'es'} activas`,
  sucursales_huerfanas_activas:(n) => `${n} sucursal${n === 1 ? '' : 'es'} huérfana${n === 1 ? '' : 's'} activas`,
  oportunidades_activas:       (n) => `${n} expediente${n === 1 ? '' : 's'} en proceso`,
  roles_contacto_activos:      (n) => `${n} rol${n === 1 ? '' : 'es'} de contacto activos`,
};

export default function BotonArchivarCliente({ tipo, id, entidad, onArchivado, variante = 'completo' }) {
  const { usuario } = useUsuario();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [bloqueo, setBloqueo] = useState(null); // { detalle: {...} }
  const [toast, setToast] = useState(null);

  // Hooks ANTES de cualquier return condicional
  const meta = TIPO_META[tipo];
  const yaArchivado = entidad?.estado === 'archivado';
  const visible = esRolDireccion(usuario) && !yaArchivado && meta;

  if (!visible) return null;

  const archivar = async () => {
    setEnviando(true);
    setError(null);
    setBloqueo(null);
    try {
      const token = localStorage.getItem('beep_token') || '';
      const r = await fetch(`${API_BASE_CLI}/api/clientes/${meta.path}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para esta acción');
        return;
      }
      if (r.status === 409 && data?.error === 'NO_PUEDE_ARCHIVAR') {
        setBloqueo({ detalle: data.detalle || {} });
        return;
      }
      if (!r.ok || !data?.ok) {
        throw new Error(data?.error || `Error ${r.status}`);
      }
      // Éxito
      setModalAbierto(false);
      setToast({ tipo: 'ok', msg: data.mensaje || 'Archivado correctamente' });
      // Espera breve para que el usuario vea el toast antes de redirigir
      setTimeout(() => {
        if (onArchivado) onArchivado();
      }, 1200);
    } catch (e) {
      setError(e.message || 'Error al archivar');
    } finally {
      setEnviando(false);
    }
  };

  const cerrarModal = () => {
    if (enviando) return;
    setModalAbierto(false);
    setError(null);
    setBloqueo(null);
  };

  // El handler hace stopPropagation por defecto para que el botón funcione
  // dentro de un contenedor clickeable (ej. tarjeta de la lista).
  const onTriggerClick = (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setModalAbierto(true);
  };

  return (
    <>
      {variante === 'compacto' ? (
        <button
          onClick={onTriggerClick}
          title={`Archivar ${meta.label}`}
          aria-label={`Archivar ${meta.label}`}
          style={{
            background: 'transparent',
            color: '#b91c1c',
            border: '1px solid transparent',
            borderRadius: 8,
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, lineHeight: 1, padding: 0,
            cursor: 'pointer',
            fontFamily: FF, flexShrink: 0,
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#fef2f2';
            e.currentTarget.style.borderColor = '#fecaca';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >🗑️</button>
      ) : (
        <button
          onClick={onTriggerClick}
          title={`Archivar ${meta.label}`}
          style={{
            background: 'transparent',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: FF,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          🗄 Archivar {meta.label}
        </button>
      )}

      {modalAbierto && (
        <ModalConfirmar
          tipo={tipo} meta={meta} entidad={entidad}
          enviando={enviando}
          error={error}
          bloqueo={bloqueo}
          onCerrar={cerrarModal}
          onConfirmar={archivar}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.tipo === 'ok' ? '#94b803' : '#dc2626',
          color: '#fff', padding: '12px 22px', borderRadius: 10,
          fontSize: 14, fontWeight: 600, fontFamily: FF, zIndex: 6000,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}>{toast.msg}</div>
      )}
    </>
  );
}

function ModalConfirmar({ tipo, meta, entidad, enviando, error, bloqueo, onCerrar, onConfirmar }) {
  const nombre = entidad?.nombre || entidad?.nombre_completo || entidad?.razon_social
    || entidad?.nombre_interno || `${meta.label} #${entidad?.id}`;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 5800, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, fontFamily: FF,
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 22px 12px' }}>
          <h3 style={{ margin: 0, color: '#270f2b', fontSize: 17, fontWeight: 700, fontFamily: FF }}>
            ¿Archivar {meta.label}?
          </h3>
          <div style={{ marginTop: 8, fontSize: 14, color: '#5e666e', lineHeight: 1.5 }}>
            <strong style={{ color: '#270f2b' }}>{nombre}</strong>
          </div>
        </div>

        <div style={{ padding: '0 22px 18px' }}>
          {bloqueo ? (
            <div style={{
              background: '#fff8ed', border: '1px solid #f0d9b8', color: '#a86b1e',
              padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No se puede archivar todavía</div>
              Tiene:
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {Object.entries(bloqueo.detalle)
                  .filter(([k, v]) => typeof v === 'number' && v > 0)
                  .map(([k, v]) => (
                    <li key={k}>{(DETALLE_HUMANO[k] || ((n) => `${n} ${k}`))(v)}</li>
                  ))}
              </ul>
              <div style={{ marginTop: 10, fontSize: 12 }}>
                Archiva primero los hijos o reasigna los expedientes antes de continuar.
              </div>
            </div>
          ) : (
            <div style={{
              fontSize: 13, color: '#5e666e', lineHeight: 1.5,
              padding: '10px 12px', background: '#f9fafb', borderRadius: 8,
              border: '1px solid #e5e7eb',
            }}>
              Esta acción ocultará al {meta.label} de las listas y typeaheads.
              No se borra físicamente — un administrador puede revertirlo después.
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 10,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: 10, borderRadius: 8, fontSize: 13,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, background: '#fafafa', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onCerrar}
            disabled={enviando}
            style={{
              background: '#fff', color: '#5e666e', border: '1px solid #e5e7eb',
              borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
              cursor: enviando ? 'not-allowed' : 'pointer', fontFamily: FF,
              opacity: enviando ? 0.5 : 1,
            }}
          >{bloqueo ? 'Entendido' : 'Cancelar'}</button>
          {!bloqueo && (
            <button
              onClick={onConfirmar}
              disabled={enviando}
              style={{
                background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                cursor: enviando ? 'not-allowed' : 'pointer', fontFamily: FF,
                opacity: enviando ? 0.6 : 1,
              }}
            >{enviando ? '⟳ Archivando…' : 'Sí, archivar'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
